"use client"
import { Box, Breadcrumbs, Stack } from "@mui/material"
import Grid from "@mui/material/Grid"
import { Link } from "../Link"
import { Typography, TypographyProps } from "../Typography"
import { dfeAccessRoutes, dfeRoutes } from "@tc/ui-permission/utils"
import { usePathname } from "next/navigation"
import { useMemo } from "react"

const allRoutes = [...dfeRoutes, ...dfeAccessRoutes]
export interface PageHeaderProps {
  title?: string
  titleTypography?: TypographyProps
  description?: string
  descriptionTypography?: TypographyProps
  disableGutters?: boolean
  actions?: React.ReactNode
  hideBreadcrumbs?: boolean
}

export const PageHeader = (props: PageHeaderProps) => {
  const {
    title,
    titleTypography,
    description,
    descriptionTypography,
    disableGutters = false,
    actions,
    hideBreadcrumbs = false,
  } = props

  const pathname = usePathname()
  const breadcrumbs = useMemo(() => {
    return allRoutes
      .filter((item) => item.path !== "/")
      .find((item) => pathname !== "/" && item.pathname.test(pathname))
  }, [pathname])

  return (
    <Stack
      sx={{
        px: disableGutters
          ? 0
          : {
              xs: 2,
              sm: 3,
              md: 4,
            },
      }}
    >
      {breadcrumbs && !hideBreadcrumbs && (
        <Box sx={{ mb: 1.5, height: 22 }}>
          <Breadcrumbs aria-label="breadcrumb" data-testid="breadcrumb">
            <Link href="/" disableGutters prefetch>
              <Typography variant="body1">Home</Typography>
            </Link>
            <Typography variant="h6" color="text.primary" active>
              {breadcrumbs.pageLabel || breadcrumbs.label}
            </Typography>
          </Breadcrumbs>
        </Box>
      )}

      {(title || actions) && (
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          columns={2}
        >
          <Grid size="grow">
            {title && (
              <Typography
                variant="h1"
                {...titleTypography}
                data-testid="page-title"
              >
                {title}
              </Typography>
            )}
          </Grid>
          <Grid size="grow">{actions}</Grid>
        </Grid>
      )}
      {description && (
        <Typography
          variant="h5"
          {...descriptionTypography}
          data-testid="page-description"
        >
          {description}
        </Typography>
      )}
    </Stack>
  )
}
